import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Calendar, XIcon } from '../icon/icon';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { ChangeEvent, useEffect, useState } from 'react';
import { InputBtn } from '../items/inputBtn';
import { RadioButtonInput, RadioInput } from '../items/radio.input';
import Select from 'react-select';
import { getCorporationList } from '../../api/report';
import { formatLocalDate } from '../../utils/util';

const radioItems = [
  { key: 'thisMonth', value: '이번달' },
  { key: 'aMonth', value: '1개월' },
  { key: 'rangeSetting', value: '기간설정' },
];

const radioBtnItems = [
  { key: 'belonged', value: '일대차' },
  { key: 'rent', value: '마당차' },
];

interface SearchConditionProps {
  handleSearchClose: () => void;
  handleSearchSubmit: (data: any) => void;
}

interface CorporationListDto {
  corporationId: number;
  corporationName: string;
}

export const DairySearchCondition = ({
  handleSearchClose,
  handleSearchSubmit,
}: SearchConditionProps) => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const { register, handleSubmit, control, setValue } = useForm({
    defaultValues: {
      monthValue: 'thisMonth',
      startDate: firstDayOfMonth,
      endDate: lastDayOfMonth,
      reportType: '',
      name: '',
    },
  });
  const [disabled, setDisabled] = useState(true);
  const [corporationList, setCorporationList] = useState<CorporationListDto[]>(
    [],
  );

  const [checkedItems, setCheckedItems] = useState<boolean[]>(
    new Array(radioBtnItems.length).fill(false),
  );

  const onSubmit = (data: any) => {
    const formattedData = {
      ...data,
      startDate: data.startDate
        ? formatLocalDate(new Date(data.startDate))
        : '',
      endDate: data.endDate ? formatLocalDate(new Date(data.endDate)) : '',
      reportType: Array.isArray(data.reportType)
        ? data.reportType.join(',')
        : data.reportType,
    };

    handleSearchSubmit(formattedData);
  };

  // 라디오 버튼 선택에 따른 날짜 설정
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value === 'thisMonth') {
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setValue('startDate', firstDayOfMonth); // react-hook-form의 setValue를 사용하여 값 업데이트
      setValue('endDate', lastDayOfMonth);
    } else if (value === 'aMonth') {
      const now = new Date();
      const oneMonthAgo = new Date(
        now.getFullYear(),
        now.getMonth() - 1,
        now.getDate(),
      );
      setValue('startDate', oneMonthAgo); // react-hook-form의 setValue를 사용하여 값 업데이트
      setValue('endDate', now);
    } else if (value === 'rangeSetting') {
      setDisabled(false); // 사용자 정의 기간 설정 활성화
    }
  };

  // 체크박스 상태를 업데이트하는 함수
  const handleCheckChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const updatedCheckedItems = checkedItems.map((item, i) =>
      i === index ? event.target.checked : item,
    );
    setCheckedItems(updatedCheckedItems);
  };

  useEffect(() => {
    const getCorporationListData = async () => {
      try {
        const response = await getCorporationList();
        setCorporationList(response);
      } catch (error) {
        console.error(error);
      }
    };

    getCorporationListData();
  }, []);

  return (
    <Container>
      <motion.div
        className="wrapper"
        initial={{ y: '100%' }}
        animate={{ y: '0%' }}
        exit={{ y: '100%' }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 40,
        }}
      >
        <div className="overlay" onClick={handleSearchClose}></div>
        <div className="wrap-shape">
          <div className="header">
            <span>검색 조건</span>
            <div className="icon-box" onClick={handleSearchClose}>
              <XIcon />
            </div>
          </div>
          <div className="body">
            <div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="range-date">
                  <RadioInput
                    register={register}
                    items={radioItems}
                    onChange={handleRadioChange}
                  />
                </div>
                <DatePickerContainer>
                  <div className="inner">
                    <Controller
                      name="startDate"
                      control={control}
                      render={({ field }) => (
                        <CustomDatePicker
                          className="search-input"
                          locale={ko}
                          dateFormat="yyyy-MM-dd"
                          selected={field.value}
                          onChange={(date: any) => field.onChange(date)} // form의 상태로 날짜 업데이트
                          disabled={disabled}
                          placeholderText="날짜 선택"
                          withPortal
                        />
                      )}
                    />
                    <div className="icon-box">
                      <Calendar />
                    </div>
                  </div>
                  <span>~</span>
                  <div className="inner">
                    <Controller
                      name="endDate"
                      control={control}
                      render={({ field }) => (
                        <CustomDatePicker
                          className="search-input"
                          locale={ko}
                          dateFormat="yyyy-MM-dd"
                          selected={field.value}
                          onChange={(date: any) => field.onChange(date)} // form의 상태로 날짜 업데이트
                          disabled={disabled}
                          placeholderText="날짜 선택"
                          withPortal
                        />
                      )}
                    />
                    <div className="icon-box">
                      <Calendar />
                    </div>
                  </div>
                </DatePickerContainer>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Select
                      className="search-select"
                      classNamePrefix="react-select"
                      options={corporationList.map((corp) => ({
                        value: corp.corporationName, // corporationName을 value로 설정
                        label: corp.corporationName, // corporationName을 label로 설정
                      }))}
                      onChange={(selectedOption) =>
                        field.onChange(
                          selectedOption ? selectedOption.value : null,
                        )
                      }
                      value={corporationList
                        .map((corp) => ({
                          value: corp.corporationName,
                          label: corp.corporationName,
                        }))
                        .find((option) => option.value === field.value)} // corporationName을 비교하여 현재 선택된 값을 설정
                      components={{ IndicatorSeparator: () => null }}
                      placeholder="회사 선택"
                      menuPlacement="top"
                      maxMenuHeight={100}
                    />
                  )}
                />
                <div className="check-box-wrapper">
                  <Controller
                    name={'reportType'}
                    control={control}
                    render={({ field }) => (
                      <>
                        {radioBtnItems.map((item, i) => (
                          <div key={item.key}>
                            <input
                              type="checkbox"
                              id={item.key}
                              {...field}
                              checked={
                                Array.isArray(field.value) &&
                                field.value.includes(item.key)
                              } // 배열에 포함된 값인지 확인
                              onChange={(event) => {
                                let updatedValue;
                                if (event.target.checked) {
                                  // 체크된 경우, 배열에 값을 추가
                                  updatedValue = Array.isArray(field.value)
                                    ? [...field.value, item.key]
                                    : [item.key]; // 처음 선택된 경우 배열로 초기화
                                } else {
                                  // 체크 해제된 경우, 배열에서 값을 제거
                                  updatedValue = Array.isArray(field.value)
                                    ? field.value.filter(
                                        (val) => val !== item.key,
                                      )
                                    : [];
                                }
                                field.onChange(updatedValue); // 배열로 상태 업데이트
                              }}
                            />
                            <label htmlFor={item.key}>{item.value}</label>
                          </div>
                        ))}
                      </>
                    )}
                  />
                </div>
                <InputBtn name="검색" type="submit" />
              </form>
            </div>
          </div>
        </div>
      </motion.div>
    </Container>
  );
};

// style
const Container = styled.div`
  display: flex;
  align-items: flex-end;
  height: 100%;
  position: relative;
  .wrapper {
    width: 100%;
    height: 100%;
    .wrap-shape {
      background: ${(props) => props.theme.textWhiteColor};
      border-radius: 2rem 2rem 0 0;
      overflow: hidden;
      padding: 0.8rem 2.2rem;
      height: 35.3rem;
      width: 100%;
      position: absolute;
      bottom: 0;
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        span {
          font-size: 1.7rem;
          font-weight: 600;
        }
        .icon-box {
          display: flex;
          transform: translateX(1.6rem);
        }
      }
      .body {
        form {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 1.2rem;
          margin-top: 0.9rem;
          input[type='text'] {
            padding: 1.4rem 1.8rem 1.5rem;
            border-radius: 0.8rem;
            border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
            &::placeholder {
              color: ${(props) => props.theme.searchPlaceholderColor};
            }
          }
          .check-box-wrapper {
            display: flex;
            gap: 2.4rem;
            input[type='checkbox'] {
              display: none;
            }
            label {
              position: relative;
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding-left: 3rem;
              font-size: 1.6rem;
              font-weight: 600;
              &::after {
                content: '';
                width: 2.2rem;
                height: 2.2rem;
                position: absolute;
                left: 0;
                top: 50%;
                transform: translateY(-50%);
                width: 2.2rem;
                height: 2.2rem;
                border-radius: 2.2rem;
                background: url('/assets/checkbox-nofill.svg');
                background-size: cover;
                background-position: center center;
                background-repeat: no-repeat;
              }
            }
            input[type='checkbox']:checked + label::after {
              background: url('/assets/checkbox-checked22px.svg');
              background-size: cover;
              background-position: center center;
              background-repeat: no-repeat;
            }
          }
        }
        .search-select {
          font-size: 1.4rem;
          .react-select__placeholder {
            color: ${(props) => props.theme.searchPlaceholderColor};
          }
          .react-select__control {
            border-color: ${(props) => props.theme.searchBoxBorderedColor};
            border-radius: 0.8rem;
            .react-select__value-container {
              padding: 1.1rem 1.6rem 0.9rem;
            }
          }
        }
      }
    }
  }
`;

const CustomDatePicker = styled(DatePicker)``;

export const DatePickerContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  span {
    margin: 0 1rem;
    font-size: 1.4rem;
    font-weight: 400;
    transform: translateY(1px);
    color: ${(props) => props.theme.joinLabelColor};
  }
  .inner {
    position: relative;
    .search-input {
      padding: 1.4rem 5rem 1.5rem 1.75rem;
      max-width: 15rem;
    }
    .react-datepicker {
      font-size: 1.4rem;
      font-weight: 400;
      .react-datepicker__header {
        background-color: ${(props) => props.theme.textWhiteColor};
      }
      .react-datepicker__current-month,
      .react-datepicker-time__header,
      .react-datepicker-year-header {
        font-size: 1.4rem;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .react-datepicker__day-name,
      .react-datepicker__day,
      .react-datepicker__time-name {
        width: 3rem;
        line-height: 3rem;
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--in-selecting-range,
      .react-datepicker__day--in-range,
      .react-datepicker__month-text--selected,
      .react-datepicker__month-text--in-selecting-range,
      .react-datepicker__month-text--in-range,
      .react-datepicker__quarter-text--selected,
      .react-datepicker__quarter-text--in-selecting-range,
      .react-datepicker__quarter-text--in-range,
      .react-datepicker__year-text--selected,
      .react-datepicker__year-text--in-selecting-range,
      .react-datepicker__year-text--in-range {
        border-radius: 2rem;
        background-color: ${(props) => props.theme.btnBlueColor};
        color: ${(props) => props.theme.textWhiteColor};
      }
    }
    .react-datepicker-popper[data-placement='bottom']
      .react-datepicker__triangle {
      fill: ${(props) => props.theme.textWhiteColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
    .icon-box {
      display: flex;
      position: absolute;
      top: 50%;
      right: 1.25rem;
      transform: translateY(-50%);
    }
  }
`;
